import { CommonModule } from '@angular/common'
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'

import { Store } from '@ngrx/store'
import {
  filter as _filter,
  flow as _flow,
  groupBy as _groupBy,
  map as _map,
  omit as _omit,
} from 'lodash/fp'
import { SelectModule } from 'primeng/select'

import { catchError, filter, forkJoin, map, of, switchMap, take, tap } from 'rxjs'

import {
  FlxButtonModule,
  FlxIconModule,
  FlxModalService,
  FlxSpinnerModule,
} from '@flowx/angular-ui-toolkit'
import {
  ConversationThreadExecutionPlanActionState,
  ConversationThreadExecutionPlanActionTargetExpressionType,
  ConversationThreadExecutionPlanActionTargetType,
  ExecutionPlanAction,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  BusinessRuleTarget,
  ExecutionPlanActionBase,
  ExpressionTarget,
  ExpressionTargetType,
  UiTarget,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import { AiExecutionPlanService } from '@flowx/designer/modules/ai/conversation/ai-execution-plan.service'
import { ConversationUtilsService } from '@flowx/designer/modules/ai/conversation/conversation-utils.service'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { ExecutionPlanActions } from '@flowx/designer/modules/ai/conversation/execution-plan.actions'
import { DocumentPluginApi } from '@flowx/designer/shared/api/document-plugin.api'
import {
  NavigationAreaNormalizedTemplateConfigs,
  NodeApiService,
} from '@flowx/designer/shared/api/node.api'
import { AppNavigationService } from '@flowx/designer/shared/app-navigation/app-navigation.service'
import {
  getNavigationTarget,
  getTargetNode,
} from '@flowx/designer/shared/app-navigation/navigation-utils'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import * as NodeActionsSelectors from '@flowx/designer/shared/store/selectors/node-actions.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectAppVersionId,
  selectProcessResourceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import {
  getActionFileId,
  getActionFileName,
  getFileIdsFromExectutionPlanActions,
} from '../../conversation/conversation.utils'
import { UploadContextFile } from '../../types/ai.types'
import { FlxSourceModalComponent } from '../source-modal/source-modal.component'

type UiExecutionPlanAction = ExecutionPlanActionBase & {
  uiTarget: {
    actionName?: string
    nodeName?: string
    elementName?: string
    hasDestination?: boolean
    isComplete?: boolean
    updateStatus: 'updated' | 'initial'
  }
  hasSource: boolean
}

type ActionCategory = {
  name: string
  state: ConversationThreadExecutionPlanActionState
  actions: UiExecutionPlanAction[]
}

@Component({
  selector: 'flx-execution-plan',
  templateUrl: './execution-plan.component.html',
  styleUrls: ['./execution-plan.component.scss'],
  host: {
    class: 'h-full block',
  },
  imports: [
    CommonModule,
    FlxButtonModule,
    FlxIconModule,
    SelectModule,
    FormsModule,
    FlxSpinnerModule,
  ],
})
export class ExecutionPlanComponent implements OnInit {
  #store = inject(Store)
  #aiExecutionPlanService = inject(AiExecutionPlanService)
  #flxModalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)
  #appNavigationService = inject(AppNavigationService)
  #nodeApiService = inject(NodeApiService)
  #documentPluginApi = inject(DocumentPluginApi)
  #conversationUtilsService = inject(ConversationUtilsService)
  // Files uploaded in-session via the source modal are cached here so we don't
  // try to download them again from /files/{id}/download — the BE-issued
  // tempFileId is not yet served by that endpoint right after the patch (the
  // action context is replaced with a docID only on the next conversation load,
  // i.e. after refresh).
  #localFilesById = signal<Record<string, File>>({})

  AgentName = AgentName
  AgentAction = AgentAction

  // @ViewChildren(Dropdown) dropdowns!: QueryList<Dropdown>

  expandedActions = new Set<string>()

  thread = this.#store.selectSignal(ConversationSelectors.defaultThread)
  executionPlanWithUpdatedTargets = this.#store.selectSignal(
    ConversationSelectors.currentExecutionPlanWithUpdatedTargets
  )
  appId = this.#store.selectSignal(selectAppId)()
  appVersionId = this.#store.selectSignal(selectAppVersionId)()
  selectedProcessResourceId = this.#store.selectSignal(selectProcessResourceId)()
  nodes = this.#store.selectSignal(nodesSelector)
  currentProcess = ProcessEffector.processDefinition$.getState
  currentExecutionPlanExists = this.#store.selectSignal(
    ConversationSelectors.currentExecutionPlanExists
  )
  messageWithCurrentExecutionPlan = this.#store.selectSignal(
    ConversationSelectors.messageWithCurrentExecutionPlan
  )
  keepDiscardOutcomeState = this.#store.selectSignal(ConversationSelectors.keepDiscardOutcomeState)
  addDefaultThreadMessageStatus = this.#store.selectSignal(
    ConversationSelectors.addDefaultThreadMessageStatus
  )

  showEnhancingPlanLoader = computed(
    () =>
      this.keepDiscardOutcomeState() === 'keeping' ||
      this.addDefaultThreadMessageStatus() === 'loading'
  )

  executionPlan = computed(() => {
    const planWithUpdates = this.executionPlanWithUpdatedTargets()
    const templateConfigs = this.templateConfigs()

    if (!planWithUpdates?.executionPlan?.actions?.length) {
      return []
    }

    return this.#categorizeActions(
      planWithUpdates.executionPlan.actions,
      templateConfigs,
      planWithUpdates.updatedActions
    )
  })
  templateConfigs = signal<NavigationAreaNormalizedTemplateConfigs>([])

  filesDataById = toSignal(
    this.#store.select(ConversationSelectors.executionPlanActions).pipe(
      map((executionPlanActions) => getFileIdsFromExectutionPlanActions(executionPlanActions)),
      switchMap((fileIds) => {
        const localFiles = this.#localFilesById()
        const idsToFetch = fileIds.filter((id) => !localFiles[id])
        if (!idsToFetch.length) {
          return of([{}])
        }
        const loadRequests = idsToFetch.map((fileId) =>
          this.#documentPluginApi.downloadFile(fileId).pipe(
            map(({ blob, name, type }) => ({
              [fileId]: new File([blob], name, { type }),
            })),
            catchError(() => of({ [fileId]: null }))
          )
        )
        return forkJoin(loadRequests)
      }),
      map((filesById) => ({ ...this.#localFilesById(), ...Object.assign({}, ...filesById) })),
      takeUntilDestroyed(this.#destroyRef)
    )
  )

  isAnyActionExecuting = computed(() => {
    const planWithUpdates = this.executionPlanWithUpdatedTargets()
    if (!planWithUpdates?.executionPlan?.actions?.length) {
      return false
    }

    return planWithUpdates.executionPlan.actions.some(
      (action) => action.state === ConversationThreadExecutionPlanActionState.Executing
    )
  })
  // TODO: decide if we'll use this
  // destinationOptions = computed(() =>
  //   this.nodes().filter((node) => [ActivityNode.USER_TASK].includes(node.type as ActivityNode))
  // )

  ActionStates = ConversationThreadExecutionPlanActionState

  ngOnInit(): void {
    this.#nodeApiService
      .getUserTaskTemplateConfigs(this.nodes().map((node) => node.navigationAreaId))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((templateConfigs) => {
        this.templateConfigs.set(templateConfigs)
      })
  }

  toggleDetails(actionId: string): void {
    if (this.expandedActions.has(actionId)) {
      this.expandedActions.delete(actionId)
    } else {
      this.expandedActions.add(actionId)
    }
  }

  async onImplementClick(action: UiExecutionPlanAction): Promise<void> {
    const node = getTargetNode({ agent: action.agent, target: action.target }, this.nodes())
    const hasDestination = this.hasDestination(action)
    const isTargetValid = this.#conversationUtilsService.isTargetValid(action.agent, action.target)
    let target = this.#getTarget(action)

    // Only navigate immediately if the target is complete (has all required fields)
    // For partial targets (only node ID), skip navigation and let the destination selector handle it
    if (hasDestination && isTargetValid) {
      const messageId = this.messageWithCurrentExecutionPlan()?.id
      if (!messageId) {
        throw new Error('No message with current execution plan found')
      }

      this.#store.dispatch(
        ExecutionPlanActions.patchExecutionPlanActionState({
          actionId: action.localId,
          actionState: ConversationThreadExecutionPlanActionState.Executing,
          messageId,
        })
      )

      await this.#appNavigationService.execute({
        id: getNavigationTarget(action.agent, action.target),
        data: {
          nodeId: node?.id,
          appId: this.appId,
          appVersionUuid: this.appVersionId,
          processVersionUuid: this.selectedProcessResourceId,
          processId: this.selectedProcessResourceId,
          navigationAreaId: node?.navigationAreaId,
          swimlaneId: node?.swimlaneDefinitionId,
          businessRuleIndex: action.target?.businessRule?.index,
          actionId: action.target?.businessRule?.actionId,
          showHideExpression:
            action.target?.expression?.expressionType ===
            ConversationThreadExecutionPlanActionTargetExpressionType.Hide,
          showDisableExpression:
            action.target?.expression?.expressionType ===
            ConversationThreadExecutionPlanActionTargetExpressionType.Disable,
          templateIds: action.target?.expression?.elementId,
          force: action.agent.agent === AgentName.AiAnalyst,
        },
      })

      if (action.target?.targetType === ConversationThreadExecutionPlanActionTargetType.Node) {
        target = { ...target, navigationAreaId: node?.navigationAreaId?.toString() }
      }
    }

    // Pass hasDestination as true only if target is complete, otherwise pass false to show destination selector
    this.#aiExecutionPlanService
      .runExecutionPlanAction(action, hasDestination && isTargetValid, target)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  onDeleteExecutionPlanClick(): void {
    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        {
          closeOnBackdropClick: false,
          backdropClass: 'overlay-backdrop',
          appendTo: 'body',
        },
        {
          title: 'Delete execution plan',
          btnOkText: 'Delete',
          btnCancelText: 'Cancel',
          btnOkType: 'danger',
        }
      )
      .data$.pipe(
        filter((data) => data.confirm),
        switchMap(() =>
          this.#aiExecutionPlanService.deleteExecutionPlan().pipe(
            tap(() => {
              this.#store.dispatch(ConversationActions.documentSelected({ document: null }))
            })
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  onClearDestinationClick(action: UiExecutionPlanAction): void {
    let newTarget = null
    if (action.target?.expression) {
      newTarget = {
        name: action.target?.name ?? '',
        targetType: action.target?.targetType ?? '',
        expression: {
          expressionType: action.target.expression.expressionType,
          elementId: null,
          nodeId: null,
        },
      }
    } else {
      newTarget = {
        name: null,
        targetType: null,
      }
    }

    this.#aiExecutionPlanService
      .patchExecutionPlanAction(action.localId, {
        target: newTarget,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.#destroyRef),
        tap(() =>
          this.#store.dispatch(
            ExecutionPlanActions.patchExecutionPlanActionTargetSuccess({
              actionId: action.localId,
              target: newTarget,
            })
          )
        )
      )
      .subscribe()
  }

  // TODO: undecided if we'll use this popup
  // onEditDestinationClick(action: UiExecutionPlanAction): void {
  //   const dropdownInstance = this.dropdowns.find(
  //     (dropdown) => dropdown.el.nativeElement.id === `node-selector-${action.localId}`
  //   )

  //   if (dropdownInstance) {
  //     dropdownInstance.show()
  //   }
  // }

  // onDestinationChange(action: UiExecutionPlanAction, event: any): void {
  //   const node = this.nodes().find((node) => node.id === event.value)
  //   if (!node) {
  //     return
  //   }

  //   const appId = this.#store.selectSignal(selectAppId)()
  //   const appVersionId = this.#store.selectSignal(selectAppVersionId)()
  //   const selectedProcessResourceId = this.#store.selectSignal(selectProcessResourceId)()
  //   this.#appNavigationService.execute({
  //     id: 'open_node',
  //     data: {
  //       nodeId: node.id,
  //       swimlaneId: node.swimlaneDefinitionId,
  //       navigationAreaId: node.navigationAreaId,
  //       appId: appId,
  //       appVersionUuid: appVersionId,
  //       processVersionUuid: selectedProcessResourceId,
  //       processId: selectedProcessResourceId,
  //     },
  //   })
  // }

  hasDestination(action: Pick<ExecutionPlanAction, 'agent' | 'target'>): boolean {
    if (action.agent.agent === AgentName.AiAnalyst) {
      return true
    }

    if (action.agent.agent === AgentName.AiDesigner) {
      const node = getTargetNode({ agent: action.agent, target: action.target }, this.nodes())
      return !!node
    }

    if (action.agent.agent === AgentName.AiDeveloper) {
      if (
        [AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(
          action.agent.action as AgentAction
        )
      ) {
        const node = getTargetNode({ agent: action.agent, target: action.target }, this.nodes())
        // Allow showing node even if actionId and index are not set - user can complete selection in chat
        return !!node
      }

      if (action.agent.action === AgentAction.Expression) {
        const node = getTargetNode({ agent: action.agent, target: action.target }, this.nodes())
        // Allow showing node even if elementId is not set - user can complete selection in chat
        return !!node
      }
    }

    return false
  }

  toggleActionSource(action: UiExecutionPlanAction): void {
    if (action.hasSource) {
      this.clearActionSource(action)
    } else {
      this.openSourceModal(action)
    }
  }

  openSourceModal(action: UiExecutionPlanAction): void {
    const fileId = getActionFileId(action)
    const filesById = this.filesDataById()
    const existingFile = fileId && filesById ? filesById[fileId] : undefined

    let existingUploadFile: UploadContextFile | undefined
    let existingFileType: 'image' | 'bpmn' | undefined

    if (existingFile && fileId) {
      const isImage = existingFile.type?.startsWith('image/')
      existingFileType = isImage ? 'image' : 'bpmn'

      existingUploadFile = {
        id: fileId,
        file: existingFile,
        name: existingFile.name,
        type: existingFile.type,
      }
    }

    const modal = this.#flxModalService.show(
      FlxSourceModalComponent,
      {
        backdropClass: 'overlay-backdrop',
        appendTo: 'body',
        panelClass: 'max-h-[80%]',
      },
      {
        action,
        initialFile: existingUploadFile,
        initialFileType: existingFileType,
      }
    )

    modal.data$
      .pipe(
        tap(({ file }: { file: UploadContextFile }) => {
          this.#localFilesById.update((curr) => ({ ...curr, [file.id]: file.file }))
          modal.close()
        }),
        switchMap(({ file, fileType }: { file: UploadContextFile; fileType: 'image' | 'bpmn' }) =>
          this.#aiExecutionPlanService.setExecutionPlanActionSource(action, file, fileType)
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  clearActionSource(action: UiExecutionPlanAction): void {
    this.#aiExecutionPlanService
      .clearExecutionPlanActionSource(action)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  getActionFile(
    action: UiExecutionPlanAction,
    filesDataById: { [key: string]: File | null }
  ): File {
    return filesDataById?.[getActionFileId(action)]
  }

  getActionFileName(action: UiExecutionPlanAction): string | undefined {
    return getActionFileName(action)
  }

  #getTarget(action: UiExecutionPlanAction): BusinessRuleTarget | ExpressionTarget | UiTarget {
    return {
      nodeId:
        parseInt(action.target?.node?.id, 10) ||
        parseInt(action.target?.businessRule?.nodeId, 10) ||
        parseInt(action.target?.expression?.nodeId, 10),
      actionId: parseInt(action.target?.businessRule?.actionId, 10),
      index: action.target?.businessRule?.index,
      templateId: parseInt(action.target?.expression?.elementId, 10),
      type: action.target?.expression?.expressionType?.toLowerCase() as ExpressionTargetType,
      navigationAreaId: action.target?.node?.navigationAreaId?.toString(),
    }
  }

  #categorizeActions(
    actions: ExecutionPlanAction[],
    templateConfigs: NavigationAreaNormalizedTemplateConfigs,
    updatedActions: Set<string>
  ): ActionCategory[] {
    const getActionCategory = (action: UiExecutionPlanAction): string | null => {
      if (action.agent.agent === AgentName.AiAnalyst) {
        return 'process'
      }
      if (action.agent.agent === AgentName.AiDesigner) {
        return 'ui'
      }
      if (action.agent.agent === AgentName.AiDeveloper) {
        return [AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(
          action.agent.action as AgentAction
        )
          ? 'businessRules'
          : 'expressions'
      }
      return null
    }

    const categoryTitles = [
      { key: 'process', name: 'Process' },
      { key: 'ui', name: 'UI' },
      { key: 'businessRules', name: 'Business Logic' },
      { key: 'expressions', name: 'Expressions' },
    ]

    const categorizedActions = _flow(_groupBy(getActionCategory), _omit([null]))(actions)

    return _flow(
      _map(({ key, name }: { key: string; name: string }) => ({
        name,
        state: (categorizedActions[key] || []).every(
          (action) => action.state === ConversationThreadExecutionPlanActionState.Finished
        )
          ? ConversationThreadExecutionPlanActionState.Finished
          : ConversationThreadExecutionPlanActionState.Pending,
        actions: (categorizedActions[key] || []).map((action) =>
          this.#addTargetData(action, templateConfigs, updatedActions)
        ),
      })),
      _filter((category) => category.actions.length > 0)
    )(categoryTitles)
  }

  #addTargetData(
    action: ExecutionPlanAction,
    templateConfigs: NavigationAreaNormalizedTemplateConfigs,
    updatedActions: Set<string>
  ): UiExecutionPlanAction {
    const nodeId =
      action.target?.node?.id ||
      action.target?.businessRule?.nodeId ||
      action.target?.expression?.nodeId
    const node = this.nodes().find((node) => node.id === parseInt(nodeId, 10))
    const actions = this.#store.selectSignal(NodeActionsSelectors.nodeActionsById(node?.id))()
    const actionName = actions?.find(
      (nodeAction) => nodeAction.id === parseInt(action.target?.businessRule?.actionId, 10)
    )?.name
    const element = action.target?.expression?.elementId
      ? templateConfigs[node?.navigationAreaId]?.[
          parseInt(action.target?.expression?.elementId, 10)
        ]
      : null

    return {
      ...action,
      uiTarget: {
        actionName: actionName ?? '',
        nodeName: node?.name,
        elementName: element?.customIdentifier ?? element?.componentIdentifier ?? '',
        hasDestination: this.hasDestination(action),
        isComplete: this.#conversationUtilsService.isTargetValid(action.agent, action.target),
        updateStatus: updatedActions.has(action.localId) ? 'updated' : 'initial',
      },
      hasSource: !!getActionFileId(action),
    }
  }
}
